import { useNavigate, useParams } from "@tanstack/react-router";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { OrderFormData } from "../../../shared/schemas/order.schema";
import { trpcClient } from "@/lib/trpc";
import { safeFormatDateInput } from "@/lib/utils";
import OrderForm from "./OrderForm";
import { toast } from "sonner";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

export default function OrderEditPage() {
  const { orderId } = useParams({ strict: false }) as { orderId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: orderData, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => {
      return trpcClient.orders.getById.query({ id: orderId });
    },
  });

  const order = orderData as any;

  const handleSubmit = async (data: OrderFormData) => {
    try {
      // Backend tự tính subtotal, vatAmount, totalAmount
      await trpcClient.orders.update.mutate({
        id: orderId,
        data: { ...data } as any,
      });

      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      await queryClient.invalidateQueries({ queryKey: ["customer-debts"] });
      navigate({ to: "/orders/$orderId", params: { orderId } });
    } catch (err: any) {
      toast.error(err?.message || "Cập nhật thất bại");
    }
  };

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !order) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Đơn hàng", to: "/orders" }]}
        entityLabel="Đơn hàng"
        backTo="/orders"
        codeOrId={orderId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !order)
    return (
      <div className="w-full p-4">
        <Skeleton className="h-96 w-full" />
      </div>
    );

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Đơn hàng", to: "/orders" },
        { label: order.orderId, to: `/orders/${orderId}` },
        { label: "Chỉnh sửa" },
      ]}
      title={`Sửa ${order.orderId}`}
      status={
        <StatusBadge type="OrderStatus" value={order.status} />
      }
      onBack={() => navigate({ to: `/orders/${orderId}` })}
      showContextPanel={false}
    >
      <div className="w-full space-y-4 animate-in fade-in duration-300">
        <OrderForm
          defaultValues={{
            orderId: order.orderId,

            customerId: order.customerId,
            customerName: order.customerName ?? "",
            contactPerson: order.contactPerson ?? "",
            phone: order.phone ?? "",
            email: order.email ?? "",
            orderDate: safeFormatDateInput(order.orderDate),
            deliveryDeadline: safeFormatDateInput(order.deliveryDeadline),
            orderDetails:
              order.orderDetails?.map((d: any) => ({
                id: d.id,
                productId: d.productId || d.product?.id || "",
                productCode: d.productCode || d.product?.productCode || "",
                productName: d.productName || d.product?.productName || "",
                specs: d.specs ?? "",
                process: d.process ?? "",
                unit: d.unit ?? "",
                quantity: d.quantity ?? 0,
                unitPrice: Number(d.unitPrice ?? 0),
                purchasePrice: d.purchasePrice != null ? Number(d.purchasePrice) : undefined,
                amount: Number(d.amount ?? 0),
                notes: d.notes ?? "",
                allowancePercent: d.allowancePercent ?? undefined,
                stockQty: d.stockQty ?? 0,
                lsxQty: d.lsxQty ?? 0,
                poReference: d.poReference ?? "",
                soReference: d.soReference ?? "",
                customerPo: d.customerPo ?? "",
                pnNo: d.pnNo ?? "",
                pjNo: d.pjNo ?? "",
                deliveryDeadline: safeFormatDateInput(d.deliveryDeadline),
                status: d.status ?? "PENDING",
                prepressCode: d.prepressCode ?? "",
                prepressPaperSize: d.prepressPaperSize ?? "",
                prepressUps: d.prepressUps ?? undefined,
                prepressPrintSheets: d.prepressPrintSheets ?? undefined,
                prepressNotes: d.prepressNotes ?? "",
              })) || [],
            vatRate: Number(order.vatRate),
            shippingFee: Number(order.shippingFee),
            currency: order.currency as "VND" | "USD",
            exchangeRate: Number(order.exchangeRate),
            deposit: order.deposit ?? "",
            paymentTerm: order.paymentTerm ?? "",
            deliveryAddress: order.deliveryAddress ?? "",
            deliveryMethod: order.deliveryMethod ?? "",
            status: order.status,
            notes: order.notes ?? "",
            salesUserId: order.salesUserId ?? "",
          }}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: `/orders/${orderId}` })}
          submitLabel={(order as any)?.status === "DRAFT" ? "Lưu" : "Cập nhật"}
          originalOrderCreatedAt={order.createdAt}
          linkedQuotation={order.quotation}
        />
      </div>
    </StandardDetailLayout>
  );
}
